import { onMounted, ref } from 'vue';
import { APPEARANCE_STORAGE_KEY, APPEARANCES } from '@/config/constants/appearance';
import type { Appearance } from '@/config/constants/appearance';

export function updateTheme(value: Appearance) {
    if (typeof window === 'undefined') {
        return;
    }

    if (value === APPEARANCES.SYSTEM) {
        const mediaQueryList = window.matchMedia('(prefers-color-scheme: dark)');
        const systemTheme = mediaQueryList.matches ? APPEARANCES.DARK : APPEARANCES.LIGHT;

        document.documentElement.classList.toggle('dark', systemTheme === APPEARANCES.DARK);
    } else {
        document.documentElement.classList.toggle('dark', value === APPEARANCES.DARK);
    }
}

const setCookie = (name: string, value: string, days = 365) => {
    if (typeof document === 'undefined') {
        return;
    }

    const maxAge = days * 24 * 60 * 60;

    document.cookie = `${name}=${value};path=/;max-age=${maxAge};SameSite=Lax`;
};

const mediaQuery = () => {
    if (typeof window === 'undefined') {
        return null;
    }

    return window.matchMedia('(prefers-color-scheme: dark)');
};

const getStoredAppearance = () => {
    if (typeof window === 'undefined') {
        return null;
    }

    return localStorage.getItem(APPEARANCE_STORAGE_KEY) as Appearance | null;
};

const handleSystemThemeChange = () => {
    const currentAppearance = getStoredAppearance();

    updateTheme(currentAppearance || APPEARANCES.SYSTEM);
};

/**
 * A `?theme=light|dark` override, applied for this page view only.
 *
 * Deliberately not persisted: it exists so a theme can be deep-linked — the
 * preview gallery renders both themes side by side in iframes — without
 * changing the viewer's own saved preference. Anything else falls through to
 * the stored value.
 */
const queryAppearance = (): Appearance | null => {
    if (typeof window === 'undefined') {
        return null;
    }

    const theme = new URLSearchParams(window.location.search).get('theme');

    return theme === APPEARANCES.DARK || theme === APPEARANCES.LIGHT ? theme : null;
};

export function initializeTheme() {
    if (typeof window === 'undefined') {
        return;
    }

    const override = queryAppearance();

    if (override) {
        updateTheme(override);

        return;
    }

    // Initialize theme from saved preference or default to system...
    const savedAppearance = getStoredAppearance();
    updateTheme(savedAppearance || APPEARANCES.SYSTEM);

    // Set up system theme change listener...
    mediaQuery()?.addEventListener('change', handleSystemThemeChange);
}

/**
 * One preference for the whole page. The Appearance tabs, the user menu, the
 * marketing header and the Guide's theme handle all read and write this ref,
 * so none of them shows a stale choice after another changes it.
 */
const appearance = ref<Appearance>(APPEARANCES.SYSTEM);
let hydrated = false;

/** Only for tests: forget the hydration so the next mount reads storage again. */
export const resetAppearanceState = (): void => {
    hydrated = false;
    appearance.value = APPEARANCES.SYSTEM;
};

export function useAppearance() {
    onMounted(() => {
        if (hydrated) {
            return;
        }

        hydrated = true;
        initializeTheme();

        const savedAppearance = localStorage.getItem(APPEARANCE_STORAGE_KEY) as Appearance | null;

        if (savedAppearance) {
            appearance.value = savedAppearance;
        }
    });

    function updateAppearance(value: Appearance) {
        appearance.value = value;

        // Store in localStorage for client-side persistence...
        localStorage.setItem(APPEARANCE_STORAGE_KEY, value);

        // Store in cookie for SSR...
        setCookie(APPEARANCE_STORAGE_KEY, value);

        updateTheme(value);
    }

    return {
        appearance,
        updateAppearance,
    };
}
